/**
 * Autopilot — SKAG preset suggestions (§2.10). Two tabs:
 *  - New:     pending suggestion cards (Apply / Not now+Undo / Dismiss).
 *  - History: every acted-on suggestion (Applied / Skipped / Not now / Dismissed).
 * Opening the section marks pending suggestions seen (zeroes the sidebar badge).
 */

import { useEffect, useMemo, useState } from "react";
import { Pager } from "@/components/Pager";
import { TranslationValue } from "@/components/TranslationCell";
import { UserAlertsBell } from "@/components/UserAlertsBell";
import {
  applyTranslationPatches,
  useTranslationPolling,
} from "@/lib/useTranslationPolling";
import { useOrgNavigate } from "@/lib/useOrgNav";
import {
  type AutopilotSuggestion,
  useApplySuggestion,
  useDismissSuggestion,
  useMarkSuggestionsSeen,
  useSnoozeSuggestion,
  useSuggestions,
  useUnsnoozeSuggestion,
} from "@/lib/api-hooks";

import { DkgCard } from "./DkgCard";
import { LabelChip } from "@/components/LabelChip";
import { CARD_UNAVAILABLE } from "@/lib/btse-ecg";
import {
  DKG_PRESET,
  DKG_PRESET_KEY,
  dkgHistoryMeta,
  dkgHistoryNote,
} from "@/lib/dkg-history";

const PRESET = "Broad to SKAG Extractor";
const CLEANER_PRESET = "Search Term Cleaner";

function icon(d: React.ReactNode, w = 14) {
  return (
    <svg width={w} height={w} viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">{d}</svg>
  );
}

function ago(iso: string): string {
  const d = (Date.now() - new Date(iso).getTime()) / 1000;
  if (d < 3600) return `${Math.max(1, Math.floor(d / 60))}m ago`;
  if (d < 86400) return `${Math.floor(d / 3600)}h ago`;
  return `${Math.floor(d / 86400)}d ago`;
}

function fmtBid(s: AutopilotSuggestion): React.ReactNode {
  if (s.payload.bid_mode === "auto08") {
    return (
      <>0.8 × avg CPT — at apply{" "}
        <b className="sg-est">≈ {s.payload.bid_estimate ?? "—"}</b></>
    );
  }
  const v = s.payload.bid_value;
  return v != null ? `$${Number(v).toFixed(2)}` : "—";
}

function Fact({ k, mono, children }: { k: string; mono?: boolean; children: React.ReactNode }) {
  return (
    <div className="sg-fact">
      <span className="sg-fact-k">{k}</span>
      <span className={"sg-fact-v" + (mono ? " is-mono" : "")}>{children}</span>
    </div>
  );
}

function fmtInt(v: number | null | undefined): string {
  return v == null ? "—" : Math.round(v).toLocaleString("en-US");
}
function fmtIpm(v: number | null | undefined): string {
  return v == null ? "—" : Number(v).toFixed(2);
}

/** The English reading of the term, on its own line beneath it (§12).
 *
 * Not drawn at all when there is nothing to draw — an empty element would still
 * take its margin and open a gap under every card whose term is already
 * English, which is most of them. What counts as "something to draw" is
 * TranslationValue's rule, not this component's. */
function TermTranslation({ s }: { s: AutopilotSuggestion }) {
  const shown =
    s.translation_status === "pending" ||
    (s.translation_status === "translated" && !!s.translation_en);
  if (!shown) return null;
  return (
    <div className="sg-tl">
      <TranslationValue status={s.translation_status} text={s.translation_en} />
    </div>
  );
}

/** Fills in cards whose term came back `pending` (§12). Shared by both tabs —
 * the poll rules are the same and neither list is the anomaly's owner. */
function useTranslatedSuggestions(
  serverRows: AutopilotSuggestion[],
): AutopilotSuggestion[] {
  const pending = useMemo(
    () =>
      serverRows
        .filter((r) => r.translation_status === "pending")
        .map((r) => r.search_term),
    [serverRows],
  );
  const patches = useTranslationPolling(pending);
  return useMemo(
    () => applyTranslationPatches(serverRows, patches, (r) => r.search_term),
    [serverRows, patches],
  );
}

function SuggestionCard({ s }: { s: AutopilotSuggestion }) {
  if (s.preset === DKG_PRESET_KEY) return <DkgCard s={s} />;
  return <PresetSuggestionCard s={s} />;
}

function PresetSuggestionCard({ s }: { s: AutopilotSuggestion }) {
  const [confirm, setConfirm] = useState(false);
  const apply = useApplySuggestion();
  const snooze = useSnoozeSuggestion();
  const dismiss = useDismissSuggestion();
  const busy = apply.isPending || snooze.isPending || dismiss.isPending;
  const isCleaner = s.preset === "search_term_cleaner";
  const name = s.payload.campaign_name ?? s.search_term;
  const budget = s.payload.daily_budget != null ? `$${Number(s.payload.daily_budget).toFixed(2)}` : "—";

  return (
    <div className="sg-card" data-state={s.status} data-preset={s.preset}>
      <div className="sg-src">
        <span className="sg-src-mark">
          {isCleaner ? CLEANER_PRESET : PRESET} · Suggestion
        </span>
        <span className="sg-rule">{s.rule_name_snapshot ?? `Rule #${s.rule_id}`}</span>
        <span className="sg-age">{ago(s.created_at)}</span>
      </div>

      {isCleaner ? (
        <>
          <div className="sg-name">{s.search_term}</div>
          <TermTranslation s={s} />
          <div className="sg-keyline">
            {s.source_campaign_name && (
              <>
                <span className="sg-from">in</span>
                <span className="sg-source">{s.source_campaign_name}</span>
              </>
            )}
          </div>
          <div className="sg-settings">
            <Fact k="Impressions (35d)" mono>{fmtInt(s.payload.impressions)}</Fact>
            <Fact k="Installs (35d)" mono>{fmtInt(s.payload.installs)}</Fact>
            <Fact k="IPM (35d)" mono>{fmtIpm(s.payload.ipm)}</Fact>
            <Fact k="Action">Exact negative · campaign level</Fact>
          </div>
        </>
      ) : (
        <>
          <div className="sg-name">{name}</div>
          <div className="sg-keyline">
            <span className="sg-kw">{s.search_term}</span>
            <span className="sg-cc">{s.country}</span>
            {s.source_campaign_name && (
              <>
                <span className="sg-from">from</span>
                <span className="sg-source">{s.source_campaign_name}</span>
              </>
            )}
          </div>
          {/* Under the keyline here, not under `.sg-name`: on this preset the
              card is titled with the campaign name and the term sits in the
              keyline, so that is what the translation belongs beneath. */}
          <TermTranslation s={s} />
          <div className="sg-settings">
            <Fact k="Daily budget" mono>{budget}</Fact>
            <Fact k="CPT bid" mono={s.payload.bid_mode !== "auto08"}>{fmtBid(s)}</Fact>
            <Fact k="Exact negative in source">{s.payload.add_negative ? "Yes" : "No"}</Fact>
            <Fact k="Product page">{s.payload.custom_product_page_id ? "Custom CPP" : "Default product page"}</Fact>
            <Fact k="On creation">{s.payload.launch_immediately ? "Live" : "Paused"}</Fact>
            {/* ТЗ BTSE §7: из текущего конфига пресета — при Apply применится он. */}
            {s.ecg_view && (
              <Fact k="Event Cost Guard">
                {s.ecg_view.unavailable ? (
                  <span className="sg-ecg-off">
                    <s>{s.ecg_view.name ?? "Deleted rule"}</s>
                    <span className="sg-ecg-warn">{CARD_UNAVAILABLE}</span>
                  </span>
                ) : (
                  s.ecg_view.name
                )}
              </Fact>
            )}
            {s.labels_view && s.labels_view.length > 0 && (
              <Fact k="Labels">
                <span className="sg-labels">
                  {s.labels_view.map((l) => (
                    <LabelChip key={l.id} name={l.name} />
                  ))}
                </span>
              </Fact>
            )}
          </div>
        </>
      )}

      {apply.data?.status === "skipped" && (
        <div className="sg-result is-neg">
          {icon(<><circle cx="12" cy="12" r="10" /><line x1="15" y1="9" x2="9" y2="15" /><line x1="9" y1="9" x2="15" y2="15" /></>)}
          <span>
            {isCleaner
              ? "Skipped — this term is already in the campaign's negative keywords."
              : "Skipped — this term is already running as an exact keyword in another campaign targeting this country."}
          </span>
        </div>
      )}

      {!confirm ? (
        <div className="sg-actions">
          <button className="sg-btn is-ghost" disabled={busy} onClick={() => setConfirm(true)}>
            Don&apos;t suggest this term again
          </button>
          <button className="sg-btn" disabled={busy} onClick={() => snooze.mutate(s.id)}>Not now</button>
          <button className="sg-btn is-primary" disabled={busy} onClick={() => apply.mutate(s.id)}>
            {apply.isPending ? "Applying…" : "Apply"}
          </button>
        </div>
      ) : (
        <div className="sg-confirm">
          <div className="sg-confirm-txt">
            <b>Dismiss &quot;{s.search_term}&quot; for this rule?</b>
            <span>This term will never be suggested again by <i>{s.rule_name_snapshot}</i>. The action cannot be undone.</span>
          </div>
          <div className="sg-confirm-acts">
            <button className="sg-btn" onClick={() => setConfirm(false)}>Cancel</button>
            <button className="sg-btn is-danger" disabled={busy} onClick={() => dismiss.mutate(s.id)}>
              Dismiss permanently
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function SgEmpty({ title }: { title: string }) {
  return (
    <div className="sg-empty">
      <span className="sg-empty-ico">{icon(<><circle cx="12" cy="12" r="10" /><path d="M12 8v4" /><path d="M12 16h.01" /></>, 20)}</span>
      <span>{title}</span>
    </div>
  );
}

function NewTab() {
  const [limit, setLimit] = useState(10);
  const [offset, setOffset] = useState(0);
  const q = useSuggestions("new", { limit, offset });
  const unsnooze = useUnsnoozeSuggestion();
  const [undo, setUndo] = useState<{ id: number; term: string } | null>(null);
  const snooze = useSnoozeSuggestion();

  const rows = useTranslatedSuggestions(q.data?.rows ?? []);
  const total = q.data?.total ?? 0;

  // Surface an Undo toast right after a snooze completes.
  useEffect(() => {
    if (snooze.isSuccess && snooze.variables != null) {
      const s = rows.find((x) => x.id === snooze.variables);
      setUndo({ id: snooze.variables, term: s?.search_term ?? "" });
      const t = setTimeout(() => setUndo(null), 8000);
      return () => clearTimeout(t);
    }
  }, [snooze.isSuccess, snooze.variables]); // eslint-disable-line react-hooks/exhaustive-deps

  // Acting on cards shrinks the list — if the page ran past the end, step back.
  useEffect(() => {
    if (total > 0 && offset >= total) {
      setOffset(Math.max(0, (Math.ceil(total / limit) - 1) * limit));
    }
  }, [total, offset, limit]);

  return (
    <>
      <div className="sg-list">
        {undo && (
          <div className="sg-toast">
            <span>&quot;{undo.term}&quot; snoozed — it can be suggested again after 7 days.</span>
            <button className="sg-btn is-ghost" onClick={() => { unsnooze.mutate(undo.id); setUndo(null); }}>
              Undo
            </button>
          </div>
        )}
        {q.isLoading ? (
          <SgEmpty title="Loading…" />
        ) : rows.length === 0 ? (
          <SgEmpty title="No new recommendations — presets will suggest campaigns here." />
        ) : (
          rows.map((s) => <SuggestionCard key={s.id} s={s} />)
        )}
      </div>
      {total > limit && (
        <Pager
          total={total}
          limit={limit}
          offset={offset}
          onOffsetChange={setOffset}
          onLimitChange={(n) => { setLimit(n); setOffset(0); }}
          pageSizes={[10, 20, 30, 50]}
        />
      )}
    </>
  );
}

const OUTCOME: Record<string, { label: string; tone: string }> = {
  applied: { label: "Applied", tone: "pos" },
  dismissed: { label: "Dismissed", tone: "mute" },
  snoozed: { label: "Not now", tone: "warn" },
  skipped: { label: "Skipped", tone: "neg" },
};

function HistoryTab() {
  const [limit, setLimit] = useState(10);
  const [offset, setOffset] = useState(0);
  const q = useSuggestions("history", { limit, offset });
  const nav = useOrgNavigate();
  const rows = useTranslatedSuggestions(q.data?.rows ?? []);
  const total = q.data?.total ?? 0;
  if (q.isLoading) return <div className="sg-list"><SgEmpty title="Loading…" /></div>;
  if (rows.length === 0 && offset === 0)
    return <div className="sg-list"><SgEmpty title="Nothing here yet." /></div>;
  return (
    <>
    <div className="sg-hist">
      {rows.map((h) => {
        const o = OUTCOME[h.status] ?? { label: h.status, tone: "mute" };
        const when = h.acted_at ?? h.created_at;
        const cleaner = h.preset === "search_term_cleaner";
        if (h.preset === DKG_PRESET_KEY) {
          const dkgOutcome =
            h.status === "dismissed"
              ? { label: "Never suggested", tone: "mute" }
              : h.status === "skipped"
                ? { label: "Skipped", tone: "warn" }
                : o;
          return (
            <div key={h.id} className="sg-hrow">
              <span className={"sg-badge is-" + dkgOutcome.tone}>{dkgOutcome.label}</span>
              <span className="sg-hterm">
                {h.search_term}
                <span className="sg-cc">{h.country}</span>
              </span>
              <span className="sg-hnote" title={dkgHistoryNote(h)}>{dkgHistoryNote(h)}</span>
              <span className="sg-hsrc">{DKG_PRESET} · {h.rule_name_snapshot ?? `Rule #${h.rule_id}`}</span>
              <span className="sg-hmeta">{dkgHistoryMeta(h)}</span>
            </div>
          );
        }
        return (
          <div key={h.id} className="sg-hrow">
            <span className={"sg-badge is-" + o.tone}>{o.label}</span>
            <span className="sg-hterm">
              {h.search_term}
              {/* Inline here rather than on its own line: a history entry is a
                  one-line log row on a fixed grid, and a second line would
                  break the column alignment the list is read by. */}
              {h.translation_status === "translated" && h.translation_en && (
                <span className="sg-tl-inline">{h.translation_en}</span>
              )}
              {!cleaner && <span className="sg-cc">{h.country}</span>}
            </span>
            <span className="sg-hnote">
              {h.status === "applied" && cleaner && (
                <>Added as exact negative to{" "}
                  <b>{h.source_campaign_name ?? "campaign"}</b></>
              )}
              {h.status === "applied" && !cleaner && (
                <>Campaign created:{" "}
                  <a href="#" className="sg-link" onClick={(e) => { e.preventDefault(); if (h.campaign_id) nav(`/ads/campaigns/${h.campaign_id}`); }}>
                    {h.payload.campaign_name ?? h.search_term}
                  </a>
                  {h.ecg_view && !h.ecg_view.unavailable && <> · ECG: {h.ecg_view.name}</>}
                  {h.labels_view && h.labels_view.length > 0 && (
                    <> · Labels: {h.labels_view.map((l) => l.name).join(", ")}</>
                  )}</>
              )}
              {h.status === "dismissed" && "Won't be suggested again"}
              {h.status === "snoozed" && `Snoozed until ${h.snooze_until ? new Date(h.snooze_until).toLocaleDateString() : "—"}`}
              {h.status === "skipped" && (cleaner
                ? "Already in this campaign's negative keywords"
                : "Already running as exact keyword in this country")}
            </span>
            <span className="sg-hsrc">{(cleaner ? CLEANER_PRESET : PRESET)} · {h.rule_name_snapshot ?? `Rule #${h.rule_id}`}</span>
            <span className="sg-hmeta">
              {new Date(when).toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })}
              {h.acted_by_email ? ` · ${h.acted_by_email}` : ""}
            </span>
          </div>
        );
      })}
    </div>
    {total > limit && (
      <Pager
        total={total}
        limit={limit}
        offset={offset}
        onOffsetChange={setOffset}
        onLimitChange={(n) => { setLimit(n); setOffset(0); }}
        pageSizes={[10, 20, 30, 50]}
      />
    )}
    </>
  );
}

export function AutopilotPage() {
  const [tab, setTab] = useState<"new" | "history">("new");
  const newQ = useSuggestions("new");
  const markSeen = useMarkSuggestionsSeen();
  const pending = newQ.data?.total ?? 0;

  // Opening the section clears the sidebar badge (seen ≠ acted).
  useEffect(() => {
    markSeen.mutate();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <>
      <div className="topbar">
        <div style={{ marginLeft: "auto", display: "flex", gap: 10, alignItems: "center" }}>
          <UserAlertsBell />
        </div>
      </div>

      <div className="content">
        <div className="ap-tabs" style={{ marginBottom: 16 }}>
          <button type="button" className="ap-tab" data-active={tab === "new"} onClick={() => setTab("new")}>
            New{pending > 0 && <span className="ap-tab-count">{pending}</span>}
          </button>
          <button type="button" className="ap-tab" data-active={tab === "history"} onClick={() => setTab("history")}>
            History
          </button>
        </div>

        {tab === "new" ? <NewTab /> : <HistoryTab />}
      </div>
    </>
  );
}
